import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, FloppyDisk, User } from "@phosphor-icons/react";
import { toast } from "sonner";

const inputCls = "h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", title: "", avatar_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        title: user.title || "",
        avatar_url: user.avatar_url || "",
      });
    }
  }, [user]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      if (refreshMe) await refreshMe();
      toast.success("Profile updated");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentLayout>
      <div className="max-w-2xl mx-auto" data-testid="profile-page">
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Account</p>
          <h1 className="font-serif text-5xl tracking-tight text-white mb-2">
            Your <span className="italic text-[#D4AF37]">profile</span>
          </h1>
          <p className="text-white/60 text-sm font-sans">
            What clients see on every itinerary you share.
          </p>
        </div>

        <form onSubmit={onSave} className="space-y-6">
          <div className="flex items-center gap-5 p-5 rounded-xl border border-[#D4AF37]/25 bg-ink-surface">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
              {form.avatar_url ? (
                <img src={form.avatar_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} />
              ) : (
                <User size={28} weight="duotone" className="text-[#D4AF37]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-xl text-white truncate">{form.name || "Your name"}</p>
              <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-sans">{form.title || "Travel Designer"}</p>
              <p className="text-xs text-white/40 font-sans truncate mt-1">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Display name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Travel Designer" data-testid="profile-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Phone</Label>
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+1 555 555 0100" data-testid="profile-phone" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Avatar URL</Label>
              <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className={inputCls} placeholder="https://…/photo.jpg" data-testid="profile-avatar" />
            </div>
          </div>

          <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-12 px-8 text-xs uppercase tracking-[0.3em]" data-testid="profile-save-btn">
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <><FloppyDisk size={16} weight="duotone" /> Save profile</>}
          </Button>
        </form>
      </div>
    </AgentLayout>
  );
}
